// ============================================================
// dev-core-web/src/components/Toast.tsx
// Version: 1.1.0
// Purpose:
//   - единичное уведомление (toast) с 4 вариантами:
//     success / error / info / warning
//   - presentation-only: variant/title/description через props
//   - НЕ делает fetch, НЕ делает mutations
//   - НЕ логирует значения (SECURITY.md §19.3)
//   - title/description — строки; никаких ReactNode, никакого
// eslint-disable-next-line no-secrets/no-secrets -- не секрет: имя React-пропа в документации файла
//     dangerouslySetInnerHTML
//
// Правила:
//   - role="alert" только для error; иначе role="status"
//     (TESTING.md §14)
//   - auto-dismiss по durationMs; 0 — не закрывать
//   - кнопка закрытия — всегда
//   - клик по toast — не действие (только close)
//   - никаких console.log
//
// Ссылки:
//   - docs/TESTING.md §14
//   - docs/SECURITY.md §19.3
//   - docs/DEVELOPMENT.md §11.2, §11.3, §11.5
//   - dev-core-web/src/hooks/useToast.ts
//   - dev-core-web/src/components/EntityCard.tsx
// ============================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Info,
  X,
} from 'lucide-react';

// ------------------------------------------------------------
// Типы
// ------------------------------------------------------------

export type ToastVariant = 'success' | 'error' | 'info' | 'warning';

export interface ToastProps {
  variant: ToastVariant;

  /** Заголовок. Обязателен. */
  title: string;

  /** Опциональное описание. Строка, не ReactNode. */
  description?: string;

  /**
   * Время автозакрытия в мс. 0 — не закрывать автоматически.
   * По умолчанию — 4000.
   */
  durationMs?: number;

  /** Callback при закрытии (вручную или по таймеру). */
  onClose?: () => void;

  /** Дополнительный класс. */
  className?: string;
}

// ------------------------------------------------------------
// Дефолты
// ------------------------------------------------------------

const DEFAULT_DURATION = 4000;
const MIN_DURATION = 0;
const MAX_DURATION = 30_000;

function clampDuration(n: number | undefined): number {
  if (typeof n !== 'number' || !Number.isFinite(n)) return DEFAULT_DURATION;
  if (n < MIN_DURATION) return MIN_DURATION;
  if (n > MAX_DURATION) return MAX_DURATION;
  return Math.floor(n);
}

// ------------------------------------------------------------
// Стили
// ------------------------------------------------------------

function variantIcon(variant: ToastVariant) {
  switch (variant) {
    case 'success':
      return <CheckCircle2 className="h-4 w-4" aria-hidden />;
    case 'error':
      return <AlertCircle className="h-4 w-4" aria-hidden />;
    case 'warning':
      return <AlertTriangle className="h-4 w-4" aria-hidden />;
    case 'info':
      return <Info className="h-4 w-4" aria-hidden />;
  }
}

function variantContainerClass(variant: ToastVariant): string {
  switch (variant) {
    case 'success':
      return 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300';
    case 'error':
      return 'border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300';
    case 'warning':
      return 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300';
    case 'info':
      return 'border-sky-200 bg-sky-50 text-sky-800 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-300';
  }
}

function variantIconClass(variant: ToastVariant): string {
  switch (variant) {
    case 'success':
      return 'text-emerald-600 dark:text-emerald-400';
    case 'error':
      return 'text-red-600 dark:text-red-400';
    case 'warning':
      return 'text-amber-600 dark:text-amber-400';
    case 'info':
      return 'text-sky-600 dark:text-sky-400';
  }
}

function variantRole(variant: ToastVariant): 'alert' | 'status' {
  return variant === 'error' ? 'alert' : 'status';
}

function variantLive(variant: ToastVariant): 'assertive' | 'polite' {
  return variant === 'error' ? 'assertive' : 'polite';
}

// ------------------------------------------------------------
// Компонент
// ------------------------------------------------------------

export function Toast({
  variant,
  title,
  description,
  durationMs,
  onClose,
  className,
}: ToastProps) {
  const duration = clampDuration(durationMs);
  const [dismissed, setDismissed] = useState(false);
  const timerRef = useRef<number | null>(null);

  const close = useCallback(() => {
    setDismissed(true);
    onClose?.();
  }, [onClose]);

  // Auto-dismiss.
  useEffect(() => {
    if (duration <= 0) return;
    if (dismissed) return;

    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      close();
    }, duration);

    return () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [duration, dismissed, close]);

  if (dismissed) return null;

  return (
    <div
      role={variantRole(variant)}
      aria-live={variantLive(variant)}
      aria-atomic="true"
      className={[
        'pointer-events-auto flex w-full max-w-sm items-start gap-2 rounded-lg border p-3 shadow-md',
        variantContainerClass(variant),
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <span className={`mt-0.5 shrink-0 ${variantIconClass(variant)}`}>
        {variantIcon(variant)}
      </span>

      <div className="flex flex-1 flex-col gap-0.5">
        <div className="text-sm font-medium">{title}</div>
        {description && (
          <div className="whitespace-pre-wrap text-xs opacity-90">
            {description}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={close}
        aria-label="Закрыть уведомление"
        title="Закрыть"
        className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded ${variantIconClass(variant)} hover:bg-black/5 dark:hover:bg-white/5`}
      >
        <X className="h-3.5 w-3.5" aria-hidden />
      </button>
    </div>
  );
}

// ------------------------------------------------------------
// Экспорт
// ------------------------------------------------------------

export default Toast;